import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "encore_access_token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setToken(t) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

/**
 * Fetch a loan document as a blob and open it. Tries a new tab first; if the
 * browser blocks the popup (Safari/iOS block window.open after an async
 * fetch), falls back to a direct download via the anchor `download`
 * attribute, which is never popup-blocked.
 */
export async function openDocument(docId, filename) {
  const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const WITHDRAWN = ["withdrawn_borrower", "withdrawn_broker", "withdrawn_lender"];

export const STATUS_TRANSITIONS = {
  submitted: ["in_review", "processing", "declined", ...WITHDRAWN],
  in_review: ["submitted", "processing", "underwriting", "declined", ...WITHDRAWN],
  processing: ["in_review", "underwriting", "declined", ...WITHDRAWN],
  underwriting: ["processing", "approved", "declined", ...WITHDRAWN],
  approved: ["underwriting", "funded", "declined", ...WITHDRAWN],
  declined: [],
  funded: [],
  withdrawn_borrower: [],
  withdrawn_broker: [],
  withdrawn_lender: [],
};

export const STATUS_LABELS = {
  submitted: "Submitted",
  in_review: "In Review",
  processing: "Processing",
  underwriting: "Underwriting",
  approved: "Approved",
  declined: "Declined",
  funded: "Funded",
  withdrawn_borrower: "Withdrawn By Borrower",
  withdrawn_broker: "Withdrawn By Broker",
  withdrawn_lender: "Withdrawn By Lender",
};

export const STATUS_PIPELINE = [
  "submitted",
  "in_review",
  "processing",
  "underwriting",
  "approved",
  "funded",
];

export const STATUS_COLORS = {
  submitted: { bg: "#E7EEFB", fg: "#1F5DC6", dot: "#1F5DC6" },
  in_review: { bg: "#F0F3F8", fg: "#5A6478", dot: "#5A6478" },
  processing: { bg: "#FFF4E0", fg: "#A05E10", dot: "#C87E1C" },
  underwriting: { bg: "#FFF4E0", fg: "#A05E10", dot: "#E59A2D" },
  approved: { bg: "#E0F2E8", fg: "#1E7D50", dot: "#1E7D50" },
  declined: { bg: "#FCE4E0", fg: "#C0392B", dot: "#C0392B" },
  funded: { bg: "#0E1525", fg: "#FFFFFF", dot: "#1F5DC6" },
  withdrawn_borrower: { bg: "#EEE9F5", fg: "#6B4FA0", dot: "#6B4FA0" },
  withdrawn_broker: { bg: "#EEE9F5", fg: "#6B4FA0", dot: "#8E6FC4" },
  withdrawn_lender: { bg: "#EEE9F5", fg: "#6B4FA0", dot: "#4F3A78" },
};

export function fmtPhone(value) {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return value;
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

export function fmtMoney(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const dOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
    if (dOnly) return `${dOnly[2]}/${dOnly[3]}/${dOnly[1]}`;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

/**
 * Format a timestamp as mm/dd/yyyy h:mm AM/PM (e.g. "06/10/2026 2:30 PM").
 *
 * Standardised across the app for any spot that previously called
 * `new Date(iso).toLocaleString()` — those calls produced different
 * formats per browser locale, which was confusing for back-office users.
 * Using a fixed mm/dd/yyyy format keeps everything legible and matches
 * what shows up on the PDF outputs.
 */
export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const s = d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "2-digit", day: "2-digit", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
    return `${s.replace(",", "")} ET`;
  } catch {
    return iso;
  }
}
